<script lang="ts">
  /**
   * The Graveyard archive (STEP_8_SPEC.md §2, D3): the chronological archive
   * of dismissed tasks, with guarded resurrection.
   *
   * Resurrection is a RE-ENTRY (post-Step-8 ruling, `IDEAS.md` #6): the modal
   * prefills the original title and the commit stays `aria-disabled` until
   * the trimmed text DIFFERS — forcing the AF1 moment, *consider carefully
   * why it was rejected*, rather than a rubber-stamp tap. The commit authors
   * exactly ONE `AppendTask` with the original as `parent_task_id` — the
   * reworded copy joins the END of the list (the writing page) while the
   * original stays `Dismissed` in its own slot (I4). `RewordTask` and
   * `RestoreTask` are never authored here; the fold's slot machinery does
   * the rest.
   *
   * Reads come from the local projection (D4) — the same half the notebook
   * renders — so an optimistic resurrection removes the row immediately and
   * rebases on the verdict like every other op. The modal mounts INLINE
   * (the plan's file list stands) with the takeover's mounting contract: the
   * body beneath is `inert`, Esc cancels and authors nothing.
   */
  import type { NotebookStore } from '../storage/operations';
  import type { Projection, Task } from '../types/generated';
  import { formatDate } from '../utils/taskStats';
  import Icon from './Icon.svelte';
  import ArrowLeft from 'lucide/dist/esm/icons/arrow-left.mjs';
  import Undo2 from 'lucide/dist/esm/icons/undo-2.mjs';
  import X from 'lucide/dist/esm/icons/x.mjs';

  interface Props {
    projection: Projection;
    store: NotebookStore;
    onBack: () => void;
  }

  let { projection, store, onBack }: Props = $props();

  /**
   * Every DISMISSED task whose lineage no longer lives, chronologically by
   * `dismissed_at` ascending — ties by page number, then line index (the
   * "chronological archive" of ARCHITECTURE.md §4). `Object.values` order is
   * task-id order, so the sort is load-bearing.
   *
   * Resurrection authors a re-entry (an `AppendTask` child), so the original
   * is crossed off this archive while a living copy descends from it — a
   * live `[ Resurrect ]` button on an already-resurrected row would invite
   * duplicate copies. When the copy settles, both highlighted lines list
   * again.
   */
  const rows = $derived.by(() => {
    const tasks = Object.values(projection.tasks);
    const parentOf = new Map(
      tasks
        .filter((t) => t.parent_task_id)
        .map((t) => [t.task_id, t.parent_task_id!]),
    );
    const hasLivingCopy = (taskId: string): boolean =>
      tasks.some((t) => {
        if (t.state !== 'Active') return false;
        let cursor = t.parent_task_id;
        while (cursor) {
          if (cursor === taskId) return true;
          cursor = parentOf.get(cursor) ?? null;
        }
        return false;
      });
    return tasks
      .filter((t) => t.state === 'Dismissed' && !hasLivingCopy(t.task_id))
      .sort(
        (a, b) =>
          (a.dismissed_at ?? 0) - (b.dismissed_at ?? 0) ||
          (projection.pages[a.page_id]?.page_number ?? 0) -
            (projection.pages[b.page_id]?.page_number ?? 0) ||
          a.line_index - b.line_index,
      );
  });

  /** The task the resurrect modal is open for, or null. */
  let resurrectingId = $state<string | null>(null);
  const resurrecting = $derived(
    resurrectingId ? projection.tasks[resurrectingId] ?? null : null,
  );
  /** The modal's draft. Prefilled with the original title. */
  let draft = $state('');

  let inputEl = $state<HTMLInputElement | undefined>(undefined);

  const changed = $derived(
    resurrecting !== null &&
      draft.trim() !== '' &&
      draft.trim() !== resurrecting.title,
  );

  function openResurrect(task: Task): void {
    resurrectingId = task.task_id;
    draft = task.title;
  }

  /** Authors nothing — Esc and `[ Cancel ]` both land here. */
  function cancel(): void {
    resurrectingId = null;
    draft = '';
  }

  /**
   * ONE `AppendTask` with the original as `parent_task_id` — a re-entry at
   * the end of the list. The original's slot is untouched (I4); the changed-
   * wording guard above is what makes this AF1, not a rubber stamp.
   */
  async function commit(): Promise<void> {
    const task = resurrecting;
    if (!task || !changed) return;
    const title = draft.trim();
    resurrectingId = null;
    draft = '';
    await store.appendTask(title, task.task_id);
  }

  $effect(() => {
    // A reword modal's primary action is editing the text.
    inputEl?.focus();
  });

  function onkeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    // Keep the container's window-level Escape from ALSO reading this as
    // leave-the-view; the modal is the more specific surface.
    event.stopPropagation();
    cancel();
  }
</script>

<section class="graveyard-view" data-testid="graveyard-view">
  <!--
    The body beneath the modal is `inert` while it is up — the takeover's
    mounting contract (D3). The modal itself mounts as a sibling below.
  -->
  <div class="graveyard-body" inert={resurrecting !== null}>
    <header class="graveyard-header">
      <button class="action" type="button" data-testid="graveyard-back" onclick={onBack}>
        <Icon node={ArrowLeft} />
        [ Back ]
      </button>
    </header>

    {#if rows.length === 0}
      <p class="graveyard-empty" data-testid="graveyard-empty">Nothing dismissed yet.</p>
    {:else}
      <ul class="graveyard-rows">
        {#each rows as task (task.task_id)}
          <li class="graveyard-row" data-testid="graveyard-row">
            <span class="graveyard-title">{task.title}</span>
            <span class="graveyard-meta">
              Page {projection.pages[task.page_id]?.page_number ?? '?'}
              · {formatDate(task.dismissed_at)}
            </span>
            <button
              class="action"
              type="button"
              data-testid="resurrect-open"
              onclick={() => openResurrect(task)}
            >
              <Icon node={Undo2} />
              [ Resurrect ]
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </div>

  {#if resurrecting}
    <div
      class="modal-backdrop"
      data-testid="resurrect-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Resurrect task"
      tabindex="-1"
      {onkeydown}
    >
      <div class="modal-sheet">
        <h2 class="resurrect-heading" id="resurrect-heading" tabindex="-1">Resurrect with new wording</h2>
        <p class="resurrect-hint">
          Rewording is the resurrection: the task is re-entered at the end of the list.
        </p>
        <input
          class="resurrect-input"
          type="text"
          aria-label="Task wording"
          data-testid="resurrect-input"
          bind:this={inputEl}
          bind:value={draft}
        />
        <div class="resurrect-actions">
          <button class="action" type="button" data-testid="resurrect-cancel" onclick={cancel}>
            <Icon node={X} />
            [ Cancel ]
          </button>
          <button
            class="action"
            type="button"
            data-testid="resurrect-commit"
            aria-disabled={!changed}
            onclick={commit}
          >
            <Icon node={Undo2} />
            [ Resurrect ]
          </button>
        </div>
      </div>
    </div>
  {/if}
</section>

<style>
  /*
   * Scoped here deliberately: the class names match the create modal's, but
   * its rules live in ITS scoped block and do not apply across components —
   * the final review caught the graveyard shipping as raw HTML because of
   * exactly that.
   */
  .graveyard-view {
    width: min(100%, 60em);
    margin: 0 auto;
    padding: 0.75rem 0 3rem;
  }

  .graveyard-header {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
  }

  .graveyard-rows {
    list-style: none;
    margin: 0;
    padding: 0;
    background: var(--paper);
    box-shadow: var(--shadow-panel);
  }

  .graveyard-row {
    display: flex;
    align-items: baseline;
    gap: 1rem;
    padding: 0.55rem 1.25rem;
    border-bottom: 1px solid color-mix(in srgb, var(--ink-muted) 18%, transparent);
  }

  .graveyard-row:last-child {
    border-bottom: none;
  }

  .graveyard-title {
    flex: 1;
    font-family: var(--font-hand, var(--font-serif, serif));
    color: var(--ink);
  }

  .graveyard-meta {
    font-size: 12px;
    color: var(--ink-muted);
    white-space: nowrap;
  }

  .graveyard-empty {
    color: var(--ink-muted);
    font-style: italic;
  }

  .modal-backdrop {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    background: color-mix(in srgb, var(--ink-muted) 20%, transparent);
    backdrop-filter: blur(2px);
    z-index: 30;
  }

  .modal-sheet {
    width: min(100%, 26em);
    background: var(--paper);
    box-shadow: var(--shadow-modal);
    padding: 1.5rem 2rem;
  }

  .resurrect-heading {
    font-size: 1.1rem;
    font-weight: normal;
    margin: 0;
    outline: none;
  }

  .resurrect-hint {
    font-size: 12px;
    color: var(--ink-muted);
    margin: 0.4rem 0 0;
  }

  .resurrect-input {
    display: block;
    width: 100%;
    margin-top: 1rem;
    font: inherit;
    font-size: 16px;
    border: none;
    border-bottom: 1px solid color-mix(in srgb, var(--ink-muted) 70%, transparent);
    background: transparent;
    padding: 2px 0;
  }

  .resurrect-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.4rem;
  }
</style>
